import { joiResolver } from '@hookform/resolvers/joi';
import { AxiosError } from 'axios';
import { Dispatch, SetStateAction, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next/';
import { useHistory, useLocation } from 'react-router-dom';
import { ResetPasswordRequest } from '../../../api/user/model';
import { ResetPasswordData } from '../../../models';
import schemaResetPassword from '../schema/resetPassword';
import RouteKey from '../../../routes/key';
import { usePostResetPassword } from '../../../api-refactored/user/usePostResetPassword';
import { UseCustomMutationReturn } from '../../../api-refactored/utils/useCustomMutation';
import { GenericErrorResponse } from '../../../api-refactored/typings';

export interface UseResetPasswordFormReturn {
  /**
   * React hook form object for reset password form
   */
  form: UseFormReturn<ResetPasswordData>;
  /** Info text for reset password form */
  infoText: string;
  /**
   * Function to trigger reset password api
   */
  resetPassword: (formData: ResetPasswordData) => void;
  /**
   * Function to update info text
   */
  setInfoText: Dispatch<SetStateAction<string>>;
  /**
   * React query mutation object
   */
  resetPasswordMutation: UseCustomMutationReturn<
    void,
    AxiosError<GenericErrorResponse>,
    ResetPasswordRequest,
    unknown
  >;
}

const { auth } = RouteKey.child;
const { login } = RouteKey.auth.child;

/**
 * A hook to handle reset password logic
 * @returns
 */
export const useResetPasswordForm = (): UseResetPasswordFormReturn => {
  const { t } = useTranslation();
  const history = useHistory();
  const [infoText, setInfoText] = useState('');
  const location = useLocation();
  const form = useForm<ResetPasswordData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: joiResolver(schemaResetPassword),
  });

  const resetPasswordMutation = usePostResetPassword({
    onSuccess: () => {
      setInfoText(
        t(
          'profile->>ResetPasswordForm.Password Reset Successful. You will be redirected shortly...'
        )
      );
      setTimeout(() => {
        history.push(`/${auth}/${login}`);
      }, 3000);
    },
    onError: (err) => failCallback(err),
  });

  const failCallback = (err: any) => {
    switch (err.response.data?.code.toString()) {
      // APPLICATION CUSTOM ERROR CODE
      case '1000':
        form.setError('root.backend', {
          type: 'backend',
          message: t(
            'profile->>ResetPasswordForm.Link is invalid or expired please request for a new link.'
          ),
        });
        break;
      case '1200':
        form.setError('root.backend', {
          type: 'backend',
          // TODO: translation
          message: err.response.data?.message?.toString(),
        });
        break;
      case '1201':
        form.setError('root.backend', {
          type: 'backend',
          message: t('profile->>ResetPasswordForm.User not found.'),
        });
        break;
      case '1008':
        form.setError('root.backend', {
          type: 'backend',
          message: t('profile->>ResetPasswordForm.User account is blocked.'),
        });
        break;
      case '1009':
        form.setError('root.backend', {
          type: 'backend',
          message: t('profile->>ResetPasswordForm.User account is locked.'),
        });
        break;
      default:
        form.setError('root.backend', {
          type: 'backend',
          message: t('profile->>ResetPasswordForm.Unknown error. Please try again.'),
        });
        break;
    }
  };

  function resetPassword(formData: ResetPasswordData) {
    setInfoText('');

    const searchParams = new URLSearchParams(location.search);
    const data: ResetPasswordRequest = {
      link: searchParams.get('id') || '',
      password: formData.password,
    };

    resetPasswordMutation.mutate(data);
  }

  return {
    resetPassword,
    form,
    infoText,
    setInfoText,
    resetPasswordMutation,
  };
};
